import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Container
} from '@mui/material';
import {
  ArrowBack,
  Save,
  LocalPharmacy,
} from '@mui/icons-material';
import { medicinesAPI } from '../../services/API';
import { useSnackbar } from 'notistack';

export const CreateMedicine = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  
  const [formData, setFormData] = useState({
    // Basic Information
    name: '',
    genericName: '',
    brand: '',
    manufacturer: '',
    strength: '',
    form: '',
    category: 'allopathy',
    schedule: 'non_scheduled',
    
    // Classification
    therapeuticClass: '',
    atcCode: '',
    hsnCode: '',
    
    // Description
    description: '',
    sideEffects: [],
    contraindications: [],
    
    // Storage
    storageConditions: {
      temperature: 'Room Temperature',
      humidity: 'Normal',
      lightSensitive: false,
      refrigerated: false,
    },
  });

  const [sideEffectInput, setSideEffectInput] = useState('');
  const [contraInput, setContraInput] = useState('');

  const steps = ['Basic Information', 'Classification', 'Safety & Storage'];

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const addSideEffect = () => {
    if (sideEffectInput.trim() && !formData.sideEffects.includes(sideEffectInput.trim())) {
      setFormData(prev => ({
        ...prev,
        sideEffects: [...prev.sideEffects, sideEffectInput.trim()]
      }));
      setSideEffectInput('');
    }
  };

  const removeSideEffect = (index) => {
    setFormData(prev => ({
      ...prev,
      sideEffects: prev.sideEffects.filter((_, i) => i !== index)
    }));
  };

  const addContraindication = () => {
    if (contraInput.trim() && !formData.contraindications.includes(contraInput.trim())) {
      setFormData(prev => ({
        ...prev,
        contraindications: [...prev.contraindications, contraInput.trim()]
      }));
      setContraInput('');
    }
  };

  const removeContraindication = (index) => {
    setFormData(prev => ({
      ...prev,
      contraindications: prev.contraindications.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await medicinesAPI.create(formData);
      enqueueSnackbar('Medicine created successfully!', { variant: 'success' });
      navigate('/hospital/pharmacy/medicines');
    } catch (error) {
      enqueueSnackbar('Error creating medicine', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const formConfigs = {
    0: {
      title: 'Basic Information',
      fields: [
        { name: 'name', label: 'Medicine Name', required: true, fullWidth: true },
        { name: 'genericName', label: 'Generic Name', required: true, fullWidth: true },
        { name: 'brand', label: 'Brand', required: true },
        { name: 'manufacturer', label: 'Manufacturer', required: true },
        { name: 'strength', label: 'Strength', required: true },
        { name: 'form', label: 'Form', required: true, type: 'select', options: [
          'tablet', 'capsule', 'syrup', 'injection', 'ointment', 'cream', 'drops', 'inhaler', 'other'
        ]},
        { name: 'category', label: 'Category', type: 'select', options: [
          'allopathy', 'ayurvedic', 'homeopathic', 'biochemic', 'other'
        ]},
        { name: 'schedule', label: 'Schedule', type: 'select', options: [
          'non_scheduled', 'schedule_h', 'schedule_h1', 'schedule_x', 'narcotic'
        ]},
      ]
    },
    1: {
      title: 'Classification & Codes',
      fields: [
        { name: 'therapeuticClass', label: 'Therapeutic Class', fullWidth: true },
        { name: 'atcCode', label: 'ATC Code' },
        { name: 'hsnCode', label: 'HSN Code' },
        { name: 'description', label: 'Description', type: 'textarea', fullWidth: true, rows: 4 },
      ]
    },
    2: {
      title: 'Safety & Storage Information',
      fields: [
        { name: 'storageConditions.temperature', label: 'Storage Temperature', value: formData.storageConditions.temperature },
        { name: 'storageConditions.humidity', label: 'Humidity Conditions', value: formData.storageConditions.humidity },
      ]
    }
  };

  const currentConfig = formConfigs[activeStep];

  return (

    <Box
          sx={{
            minHeight: "100vh",
            background: "linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)",
          }}
        >
        <Container maxWidth="xl" sx={{ py: 5 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/hospital/pharmacy/medicines')}
          variant="outlined"
        >
          Back
        </Button>
        <Typography variant="h3" fontWeight="bold" sx={{ flexGrow: 1 }}>
          Add New Medicine
        </Typography>
      </Box>


      <Card>
        <CardContent>
          {/* Stepper */}
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Typography variant="h4" gutterBottom fontWeight="bold">
            {currentConfig.title}
          </Typography>

          <Grid container spacing={3}>
            {/* Dynamic Form Fields */}
            {currentConfig.fields.map((field) => (
              <Grid item xs={12} sm={field.fullWidth ? 12 : 6} key={field.name}>
                {field.type === 'select' ? (
                  <TextField
                    select
                    fullWidth
                    label={field.label}
                    value={formData[field.name]}
                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                    required={field.required}
                  >
                    {field.options.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option.charAt(0).toUpperCase() + option.slice(1).replace('_', ' ')}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : field.type === 'textarea' ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={field.rows || 4}
                    label={field.label}
                    value={formData[field.name]}
                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                  />
                ) : (
                  <TextField
                    fullWidth
                    label={field.label}
                    value={field.value || formData[field.name]}
                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                    required={field.required}
                  />
                )}
              </Grid>
            ))}

            {/* Side Effects (Step 2) */}
            {activeStep === 2 && (
              <Grid item xs={12}>
                <Typography variant="h5" gutterBottom>
                  Side Effects
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    value={sideEffectInput}
                    onChange={(e) => setSideEffectInput(e.target.value)}
                    placeholder="Add side effect"
                    onKeyPress={(e) => e.key === 'Enter' && addSideEffect()}
                    sx={{ flexGrow: 1 }}
                  />
                  <Button onClick={addSideEffect}>
                    Add
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {formData.sideEffects.map((effect, index) => (
                    <Chip
                      key={index}
                      label={effect}
                      onDelete={() => removeSideEffect(index)}
                      color="warning"
                    />
                  ))}
                </Box>
              </Grid>
            )}

            {/* Contraindications (Step 2) */}
            {activeStep === 2 && (
              <Grid item xs={12}>
                <Typography variant="h5" gutterBottom>
                  Contraindications
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    value={contraInput}
                    onChange={(e) => setContraInput(e.target.value)}
                    placeholder="Add contraindication"
                    onKeyPress={(e) => e.key === 'Enter' && addContraindication()}
                    sx={{ flexGrow: 1 }}
                  />
                  <Button onClick={addContraindication}>
                    Add
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {formData.contraindications.map((contra, index) => (
                    <Chip
                      key={index}
                      label={contra}
                      onDelete={() => removeContraindication(index)}
                      color="error"
                    />
                  ))}
                </Box>
              </Grid>
            )}

            {/* Storage Conditions (Step 2) */}
            {activeStep === 2 && (
              <Grid item xs={12}>
                <Typography variant="h5" gutterBottom>
                  Storage Conditions
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Light Sensitive</InputLabel>
                      <Select
                        value={formData.storageConditions.lightSensitive}
                        onChange={(e) => handleInputChange('storageConditions.lightSensitive', e.target.value)}
                        label="Light Sensitive"
                      >
                        <MenuItem value={false}>No</MenuItem>
                        <MenuItem value={true}>Yes</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Refrigerated</InputLabel>
                      <Select
                        value={formData.storageConditions.refrigerated}
                        onChange={(e) => handleInputChange('storageConditions.refrigerated', e.target.value)}
                        label="Refrigerated"
                      >
                        <MenuItem value={false}>No</MenuItem>
                        <MenuItem value={true}>Yes</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>

          {/* Navigation Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
            >
              Back
            </Button>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={loading}
                  startIcon={<Save />}
                >
                  {loading ? 'Creating...' : 'Create Medicine'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                >
                  Next
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
      </Container>
    </Box>
    
  );
};